<template>
  <div :class="['object', initializing ? 'fade-in' : '', holdingThis ? 'dragging' : '']"
      :key="value.key" :style="style"
      @click="clickInside"
      v-click-outside="clickOutside">
    <div class="hold-on" ref="holdon" :style="holdOnStyle" v-if="hasHoldOn"></div>
    <div v-if="canEdit" class="options">
      <button v-if="canDelete" class="round-button delete-button" :disabled="!canEdit" @click="delete_">
        <font-awesome-icon size="xs" icon="trash" color="white" />
      </button>
      <button v-if="canChangeColour" class="round-button settings-button" :disabled="!canEdit" @click="showingSettings = !showingSettings">
        <font-awesome-icon size="xs" icon="bars" color="white" />
      </button>
      <button v-if="canLock && isOwner" class="round-button lock-button" @click="toggleLock">
        <font-awesome-icon size="xs" :icon="lockIcon" color="white" />
      </button>
    </div>

    <component :is="`f-${value.type}`"
      ref="subtype"
      class="content"
      :data="value"
      :canEdit="canEdit"
      @input="update"></component>

    <object-settings v-if="showingSettings" :data="value" :width="value.width"></object-settings>

    <span class="name-label" style="ownerLabelStyle">{{ownerName}}</span>
    <div ref="resize" class="resize"></div>
  </div>
</template>

<script>

import TextObject from './TextObject.vue';
import GifObject from './GifObject.vue';

import ObjectSettings from './ObjectSettings.vue'

import _ from 'lodash'

const ALLOWED_TYPES = ['text', 'gif'];

export default {
  components: {
    'f-text': TextObject,
    'f-gif': GifObject,
    'object-settings': ObjectSettings
  },
  props: {
    id: {
      type: String,
      required: true
    },
    ownerId: {
      type: String,
      required: true
    },
    ownerName: {
      type: String,
      required: false,
      default: () => 'Anonymous'
    },
    value: {
      type: Object,
      default: () => {
        return {}
      }
    },
    type: {
      type: String,
      required: true,
      validator: (type) => ALLOWED_TYPES.includes(type)
    },
    anchor: {
      type: Object,
      required: true,
      validator: (object) => Object.keys(object).includes('x') && Object.keys(object).includes('y')
    }
  },
  data() {
    return {
      userId: localStorage.getItem('userId'),
      holdingThis: false,
      holdingOffset: {
        x: 0,
        y: 0
      },
      resizingThis: false,
      resizingStart: {
        x: 0,
        y: 0
      },
      initializing: true,
      hasHoldOn: true,
      showingSettings: false,
      updatingPositionTimeout: false,
      updatingResizeTimeout: false,
      canChangeColour: true,
      canDelete: true,
      canLock: true,
      locked: false,
      previousMouseHoldPosition: {
        x: 0,
        y: 0
      },
      draggingDirection: 0
    }
  },
  methods: {
    startHold(x, y) {
      if (!this.canEdit) return;

      this.holdingThis = true

      this.moveToFront();

      const boundingClientRect = this.$refs.holdon.getBoundingClientRect();

      this.holdingOffset = {
        x: x - boundingClientRect.x,
        y: y - boundingClientRect.y
      }
    },
    endHold(_x, _y) {
      if (!this.canEdit) return;

      this.holdingThis = false;
      this.resizingThis = false;
    },
    onHold(x, y) {
      if (!this.canEdit) return;

      if (this.holdingThis) {
        if (x === 0 || y === 0 || x >= this.$parent.width - 1 || y >= this.$parent.height - 1) {
          this.holdingThis = false;
          return;
        }

        this.value.x = x - this.holdingOffset.x - this.anchor.x;
        this.value.y = y - this.holdingOffset.y - this.anchor.y;

        this.draggingDirection = this.previousMouseHoldPosition.x - x  < 0 ? 1 : -1;

        if (this.updatingPositionTimeout) return;

        this.update({
          x: x - this.holdingOffset.x - this.anchor.x,
          y: y - this.holdingOffset.y - this.anchor.y
        });

        this.updatingPositionTimeout = true;

        setTimeout(() => { this.updatingPositionTimeout = false; }, 10);
      }

      if (this.resizingThis) {
        this.value.width = this.value.width + (x - this.resizingStart.x);
        this.value.height = this.value.height + (y - this.resizingStart.y);

        this.resizingStart = {
          x,
          y
        }

        if (this.updatingResizeTimeout) return;

        this.update({
          width: this.value.width,
          height: this.value.height
        });

        this.updatingResizeTimeout = true;

        setTimeout(() => { this.updatingResizeTimeout = false; }, 10);
      }

      this.previousMouseHoldPosition = {
        x,
        y
      }
    },
    startResize(x, y) {
      this.resizingThis = true;
      this.resizingStart = {
        x,
        y
      }
    },
    applyUpdate(changes) {
      if (Object.keys(changes).includes('locked')) {
        this.locked = changes.locked;
      }

      this.$refs.subtype.applyUpdate(changes);
    },
    update(changes) {
      this.connection.emit('object_update', { id: this.id, changes });
    },
    delete_() {
      this.$parent.deleteObject(this.id);
    },
    moveToFront() {
      if (!this.canEdit) return;

      this.update({ zIndex: 1 });
      this.value.zIndex = 1;
    },
    moveToBack() {
      if (!this.canEdit) return;

      this.update({ zIndex: 0 });
      this.value.zIndex = 0;
    },
    clickInside() {
      if (!this.canEdit) return;

      if (this.value.zIndex === 0)
        this.moveToFront();
    },
    clickOutside() {
      if (!this.canEdit) return;

      this.$refs.subtype.onClickOutside();

      if (this.value.zIndex === 1)
        this.moveToBack();
    },
    toggleLock() {
      if (!this.canEdit) return;

      this.locked = !this.locked;

      this.update({
        locked: this.locked
      })
    }
  },
  computed: {
    style: function() {
      return `
        left:${this.anchor.x + this.value.x}px;
        top:${this.anchor.y + this.value.y}px;
        height:${this.value.height}px;
        width:${this.value.width}px;
        background-color:${this.value.backgroundColour};
        color: ${this.value.textColour}!important;
        z-index: ${this.value.zIndex};
        transition: transform .2s ease;
      `;
    },
    openSettingsStyle: function() {
      return `
        background-color:${this.value.backgroundColour};
      `;
    },
    holdOnStyle: function() {
      return this.canEdit ? 'cursor:grab;' : ''
    },
    canEdit: function() {
      return !this.locked || this.ownerId === this.userId;
    },
    isOwner: function() {
      return this.userId === this.ownerId;
    },
    lockIcon: function() {
      return this.locked === true ? 'lock' : 'lock-open';
    },
    ownerLabelStyle: function() {
      return `
        color: ${this.value.textColour};
      `;
    }
  },
  mounted() {
    this.locked = this.value.locked;

    this.initializing = true;
    setTimeout(() => {
      this.initializing = false;
    }, 200);
  }
}

</script>

<style scoped>

.object {
  position: absolute;
  border-radius: 6px;
  -webkit-box-shadow: 0px 0px 17px -2px rgba(0,0,0,0.49);
  box-shadow: 0px 0px 17px -2px rgba(0,0,0,0.49);
}

.dragging > .hold-on {
  transition: 200ms;

  background-color: rgba(0, 0, 0, 0.5);
}

.hold-on {
  height: 20px;

  transition: 200ms;
  background-color: rgba(0, 0, 0, 0.3);

  margin: 4px;
  border-radius: 6px;

  position: relative;

  transition: 50ms;
}

.options {
  position: absolute;
  right: -40px;
  top: 0px;
  width: 50px;
  height: 100px;

}

.options > * {
  display: none;
}

.object:hover > .options> *, .options:hover > * {
  display: inline;
}


.delete-button {
  position: absolute;
  right: 0;
  top: 24px;

  background-color: rgb(182, 1, 1);
}

.settings-button {
  position: absolute;
  right: 0;
  top: 58px;

  background-color: rgb(1, 146, 182);
}

.lock-button {
  position: absolute;
  right: 0;
  top: 94px;

  background-color: rgb(102, 102, 102);
}

.delete-button:hover {
  background-color: rgb(109, 0, 0);
}

.settings-button:hover {
  background-color: rgb(0, 105, 109);
}

.lock-button:hover {
  background-color: rgb(58, 58, 58);
}

.content {
  height: calc(100% - 40px);
  padding: 5px;
}

span.name-label {
  position: absolute;
  bottom: 5px;
  left: 5px;
  font-size: 10px;
}

.resize {
  position: absolute;
  right: -5px;
  bottom: -5px;
  width: 15px;
  height: 15px;
  cursor: se-resize!important;
}

</style>



// WEBPACK FOOTER //
// src/components/Object.vue